<script setup>
import { usePageHead } from '@composables/use-page-head'
import { computed, onMounted, ref } from 'vue'
import { useRouter } from 'vue-router'
import { useAppearanceStore } from '@state/stores/appearance'
import { useMemberStore } from '@state/stores/member'
import { usePremiumStore } from '@state/stores/premium'
import { useMessages } from '@composables/use-messages'
import { radioStreamQualities } from '@/configuration'
import DataLayer from '@services/data-layer/index'
import externalPlayerTracker from '@services/mixpanel/external-player-tracker'
import bannerTracker from '@services/mixpanel/banner-tracker'
import { useAnalytics } from '@composables/use-analytics'
import copyToClipboard from '@utils/copy-to-clipboard'

const UPSELL_SOURCE = 'settings-external-players'
const qualityOptionLabel = quality => `${quality.label} · ${quality.bitrate}`

const router = useRouter()
const appearanceStore = useAppearanceStore()
const memberStore = useMemberStore()
const premiumStore = usePremiumStore()
const analyticsService = useAnalytics()
const getMessageText = useMessages()

const qualities = radioStreamQualities
const formats = ['pls', 'm3u']
const selectedQualityKey = ref('mp3_high')
const selectedFormat = ref('pls')
const stations = ref([])
const isLoading = ref(false)
const loadFailed = ref(false)

const memberId = computed(() => memberStore.currentMember.id)
const isPremium = computed(() => premiumStore.isPremium)
const hasEverBeenPremium = computed(() => premiumStore.hasEverBeenPremium)
const ctaText = computed(
    () =>
        hasEverBeenPremium.value
            ? 'Go Premium'
            : getMessageText('PREMIUM_UPSELL_CTA_FREE_TRIAL')
)
const qualityOptions = computed(() => qualities.map(qualityOptionLabel))
const activeQuality = computed(
    () => qualities.find(quality => quality.key === selectedQualityKey.value)
)
const activeQualityOption = computed(
    () => activeQuality.value ? qualityOptionLabel(activeQuality.value) : ''
)
const activeQualityLabel = computed(() => activeQuality.value?.label || '')

function selectedQuality(station) {
    return (station.qualities || []).find(quality => quality.id === selectedQualityKey.value)
}

function playlistUrl(station) {
    return selectedQuality(station)?.playlists?.[selectedFormat.value] || ''
}

function streamUrl(station) {
    return selectedQuality(station)?.url || ''
}

function hasSelectedQuality(station) {
    return Boolean(selectedQuality(station))
}

function handleQualityChange(option) {
    const selected = qualities.find(quality => qualityOptionLabel(quality) === option)
    if (selected) selectedQualityKey.value = selected.key
}

function handleDownload(station) {
    if (!playlistUrl(station)) return

    externalPlayerTracker.onPlaylistDownloaded({
        channel: station.id,
        quality: selectedQualityKey.value,
        format: selectedFormat.value,
    })
}

function handleCopyUrl(station) {
    const url = streamUrl(station)
    if (!url) return

    copyToClipboard(url)
    externalPlayerTracker.onUrlCopied({
        channel: station.id,
        quality: selectedQualityKey.value,
    })
    appearanceStore.handleNotification({
        message: getMessageText('STREAM_URL_COPIED'),
        type: 'success',
    })
}

function handleGoPremium() {
    analyticsService.premiumUpsellInitiated(UPSELL_SOURCE)
    bannerTracker.onBannerTapped(UPSELL_SOURCE)
    router.push({ name: 'premium/view-plans', query: { from: UPSELL_SOURCE } })
}

async function loadStreamUrls() {
    isLoading.value = true
    loadFailed.value = false

    try {
        const response = await DataLayer.streamUrls.getByMemberId(memberId.value)
        stations.value = response.body.stations
    } catch (error) {
        loadFailed.value = true
        console.error('Failed to load stream urls', error)
    } finally {
        isLoading.value = false
    }
}

onMounted(() => {
    externalPlayerTracker.onPageViewed(isPremium.value)

    if (isPremium.value) {
        loadStreamUrls()
        return
    }

    bannerTracker.onBannerSeen(UPSELL_SOURCE)
})

usePageHead({ title: 'Settings - External Players', description: 'Settings - External Players' })
</script>

<template>
    <BaseSection padded>
        <template v-if="isPremium">
            <h6 class="subtitle">External Players</h6>
            <p class="intro">
                Play FRISKY radio channels on an external player like Winamp, VLC, or any hardware
                device that accepts pls or m3u files.
            </p>

            <div class="controls">
                <BaseCustomSelect
                    class="quality"
                    :options="qualityOptions"
                    :active="activeQualityOption"
                    prefix="Audio Quality"
                    @change="handleQualityChange"
                />
                <div
                    class="formats"
                    role="group"
                    aria-label="Playlist format"
                >
                    <button
                        v-for="format in formats"
                        :key="format"
                        :class="['format', { 'format-active': selectedFormat === format }]"
                        :aria-pressed="selectedFormat === format ? 'true' : 'false'"
                        type="button"
                        @click="selectedFormat = format"
                    >.{{ format }}</button>
                </div>
            </div>

            <p
                v-if="isLoading"
                class="state"
            >Loading your stream links…</p>
            <p
                v-else-if="loadFailed"
                class="state"
            >
                We couldn't load your stream links.
                <button
                    class="action"
                    type="button"
                    @click="loadStreamUrls"
                >Try again</button>
            </p>
            <ul
                v-else
                class="channels"
            >
                <li
                    v-for="station in stations"
                    :key="station.id"
                    class="channel"
                >
                    <span class="identity">
                        <span :class="['dot', station.id]" />
                        <span class="channel-title">{{ station.title }}</span>
                    </span>
                    <span class="actions">
                        <template v-if="hasSelectedQuality(station)">
                            <!-- A plain navigation, never a fetch: the playlist endpoints send no
                                 CORS headers, and their Content-Disposition header is what makes
                                 the browser save the file instead of opening it.

                                 `target="_blank"` is load-bearing, not a nicety: a SAME-tab
                                 navigation makes Firefox abort the playing radio stream's fetch —
                                 the audio element errors (MEDIA_ERR_ABORTED), SoundManager2 reports
                                 onload(false), and player-service stops the player — even though the
                                 navigation itself is canceled into a download and the page survives.
                                 The throwaway tab keeps the download off this document entirely (and
                                 shows any error response there instead of navigating the SPA away).

                                 The `v-if` is the last line of defence on the href: `hasSelectedQuality`
                                 vouches for the quality being there, not for it being whole, so a
                                 malformed payload can still leave `playlistUrl` with nothing to return.
                                 An `href=""` is NOT an inert link — the html spec resolves the empty
                                 string against the current document, so clicking one would open a
                                 pointless duplicate of the app in the new tab. No url, no anchor. -->
                            <a
                                v-if="playlistUrl(station)"
                                class="download"
                                :href="playlistUrl(station)"
                                target="_blank"
                                rel="noopener"
                                @click="handleDownload(station)"
                            >Download</a>
                            <button
                                class="copy"
                                type="button"
                                @click="handleCopyUrl(station)"
                            >Copy URL</button>
                        </template>
                        <!-- Nothing to download and nothing to copy: say so, and name the quality
                             to change away from, rather than leave two controls that do nothing. -->
                        <span
                            v-else
                            class="unavailable"
                        >Not available in {{ activeQualityLabel }}</span>
                    </span>
                </li>
            </ul>
        </template>

        <!-- A free member has no stream links to show — the api refuses them — so the tab
             makes the case for the ones they could have. Nothing in this branch touches
             `stations`, reads a url, or renders a control that could hand one over. -->
        <template v-else>
            <div class="pitch">
                <h6 class="subtitle pitch-title">Play FRISKY radio channels anywhere</h6>
                <p class="intro">
                    Play FRISKY radio channels in external players like Winamp, VLC, vTuner or any
                    player or hardware device that accepts direct pls or m3u links. Another way FRISKY
                    Premium is the best way to enjoy the best underground electronic music on the planet.
                </p>

                <!-- The premium banner's CTA, to the prop (premium-banner.vue): one Go Premium
                     button, one look, wherever a member meets it. Bought with `size` and
                     `button-type` rather than a css override, because the two compile to sibling
                     compound selectors of EQUAL specificity — a call-site override would be a
                     source-order coin toss.

                     The click handler has to be bound here: it falls through to BaseButton's
                     own `<button>`, and its absence is what BaseButton's `isLabel` computed
                     reads to apply `cannotBeHovered` — `pointer-events: none`. Without it
                     this is a CTA nothing can click. -->
                <!-- The wrapper, not the button, owns the mobile centering: BaseButton's
                     `noMargins`/`autoWidth` compound selectors would out-rank (or coin-toss)
                     a call-site margin override, but a flex wrapper never competes with them. -->
                <div class="pitch-cta">
                    <BaseButton
                        :modifiers="{ noMargins: true, autoWidth: true }"
                        button-type="primaryFlat"
                        size="hero"
                        @click="handleGoPremium"
                    >{{ ctaText }}</BaseButton>
                </div>
            </div>
        </template>
    </BaseSection>
</template>

<style lang="scss" scoped>
@use '@design' as *;

// The channel row's two filled controls (Figma 860:33). Deliberately NOT BaseButtons:
// BaseButton renders a <button>, and Download has to stay a plain <a href> — the playlist
// endpoints send no CORS headers, so only an anchor navigation lets the api's
// Content-Disposition header save the file; a button would have nothing to click to.
// So the pair borrows BaseButton's tokens instead of the component — its radius, its 600
// weight, and the exact hover targets of its `highlight` and `white` types — reached with
// a plain background swap, the treatment BaseButton's own `.secondaryPink` and `.nadir`
// types use rather than its ::before/::after cross-fade. One mixin, so an anchor and a
// button that must look identical cannot drift apart.
@mixin rowButton {
    @extend %contentSubtitleM;

    display: flex;
    align-items: center;
    justify-content: center;
    // Sized to the row rather than to the design system: one-off literals, like
    // `.quality`'s width below, that no $size-* token elsewhere would ever consume. The
    // min-width is what holds the two buttons to an even, matched pair — narrower on a
    // phone, where every pixel it takes is one the channel name gives up, but never
    // narrower than "Download" sets, or it stops binding and the pair goes uneven.
    min-width: 96px;
    height: 40px;
    padding: 0 $s-s;
    font-weight: 600;
    white-space: nowrap;
    text-decoration: none;
    cursor: pointer;
    border: 0;
    border-radius: $button-border-radius;
    transition: background 0.3s ease-in-out, transform 0.1s ease-in-out;

    // The press every other button in the app has (_base-button.vue's `.button`).
    &:active {
        transform: scale(0.95);
    }

    @include tablet {
        min-width: 104px;
        padding: 0 $s-m;
    }
}

.subtitle {
    @extend %contentSubtitleS;

    min-width: 100%;
    margin: 0;
    text-align: center;

    @include tablet {
        text-align: left;
    }
}

.intro {
    @extend %contentSubtitleM;

    margin: 0 0 $s-xl;
    color: $c-text-faded;
}

.controls {
    display: flex;
    flex-direction: column;
    gap: $s-s;
    align-items: stretch;
    margin-bottom: $s-xl;

    @include tablet {
        flex-direction: row;
        align-items: center;
        justify-content: space-between;
    }
}

// Wide enough for the "Audio Quality" label and the longest option ("HI-FI · 320 kbps")
// side by side, so that the select — and the format toggle beside it — stay put as the
// selection changes, and the value never folds onto a second line. A one-off layout width
// with no design-system meaning: kept a literal rather than promoted to a global $size-*
// token nothing else would ever consume.
.quality {
    @include tablet {
        width: 260px;
    }
}

// A two-option segmented toggle rather than a dropdown — the whole page follows it,
// so both formats stay visible and one tap away.
.formats {
    display: flex;
    align-items: stretch;
    border: 1px solid $c-shark;
    border-radius: $size-radius-m;
}

.format {
    @extend %contentSubtitleM;

    flex: 1;
    padding: $s-xs $s-l;
    color: $c-text-faded;
    cursor: pointer;
    background: none;
    border: 0;
    border-radius: $size-radius-m;

    &:hover {
        color: $c-white;
    }
}

.format-active {
    color: $c-white;
    background: $c-highlight;
}

.state {
    @extend %contentSubtitleM;

    margin: 0;
    color: $c-text-faded;
}

.channels {
    padding: 0;
    margin: 0;
    list-style: none;
}

// A row at every width, phone included: the actions belong beside the channel they act
// on, not stacked under it. It is a tight fit on a narrow phone — two buttons and a name
// — so the actions hold their size and the name gives way (see .identity below).
.channel {
    display: flex;
    gap: $s-xs;
    align-items: center;
    justify-content: space-between;
    padding: $s-m $s-s;
    background: $c-steel-grey;
    border-radius: $size-radius-m;

    ~ .channel {
        margin-top: $s-xs;
    }

    // Alternating fills, so the four stacked cards read as four rows and not one slab.
    // The rows used to be a flat $c-alt-bg, which was too dark to tell apart; that darker
    // shade now does the contrast job on every second row instead.
    &:nth-child(even) {
        background: $c-alt-bg;
    }

    @include tablet {
        padding: $s-m $s-l;
    }
}

// `min-width: 0` is what lets the title truncate instead of shouldering the buttons off
// the row: a flex item will not shrink below its content unless told it may.
.identity {
    display: flex;
    flex: 1;
    gap: $s-xs;
    align-items: center;
    min-width: 0;

    @include tablet {
        gap: $s-s;
    }
}

// The channel's gradient carries its identity — the row is otherwise deliberately plain.
.dot {
    @include square($s-m);

    flex-shrink: 0;
    border-radius: 50%;
}

.frisky {
    background: $c-frisky-channel-grad;
}

.deep {
    background: $c-deep-channel-grad;
}

.chill {
    background: $c-chill-channel-grad;
}

.classics {
    background: $c-classics-channel-grad;
}

// Bold, and set outright rather than reached for from a mixin: h4 is 24px but weight 900,
// h5 only reaches 24px on desktop — neither is a fixed size at 700.
// The design says 24px, but its row is 96px tall where ours is half that, so 24px reads
// far heavier here than it does there; 18px carries the row without swamping it. The phone
// gets 16px because the buttons now sit beside the name and there is not the room: at 24px
// "Classics" needed 116px of the ~70px left over.
.channel-title {
    overflow: hidden;
    text-overflow: ellipsis;
    font-size: 16px;
    font-weight: 700;
    line-height: 24px;
    color: $c-white;
    white-space: nowrap;

    @include tablet {
        font-size: 18px;
        line-height: 26px;
    }
}

// Never squashed: the pair keeps its size and the channel title truncates instead. The
// gap tightens on a phone, where the two buttons and the name are competing for ~320px.
.actions {
    display: flex;
    flex-shrink: 0;
    gap: $s-xs;
    align-items: center;

    @include tablet {
        gap: $s-l;
    }
}

// The page's one inline text control: "Try again", set inside the failed-load sentence,
// where a filled button would read as a CTA competing with the rows it is standing in for.
// (Download and Copy URL were styled from here too, until they became real buttons.)
.action {
    @extend %contentSubtitleS;

    padding: 0;
    color: $c-highlight;
    text-decoration: none;
    cursor: pointer;
    background: none;
    border: 0;

    &:hover {
        color: $c-white;
    }
}

// The row's filled pair — pink Download, white Copy URL — with the hover targets lifted
// off BaseButton's `highlight` and `white` types so they read as the same buttons.
.download {
    @include rowButton;

    color: $c-white;
    background: $c-highlight;

    &:hover {
        background: lighten($c-highlight, 10%);
    }
}

.copy {
    @include rowButton;

    color: $c-black;
    background: $c-white;

    &:hover {
        background: darken($c-white, 10%);
    }
}

// The quiet counterpart to `.action`: a row with no url in the selected quality states
// that plainly, in the faded body colour, where its two controls would have been.
// (%contentSubtitleS already carries $c-text-faded.)
.unavailable {
    @extend %contentSubtitleS;
}

// FREE STATE — the premium pitch: one column of the argument, the players it opens FRISKY
// up to, and the CTA.
// Constrained rather than left to fill the pane. The settings pane is `width: 100%` of the
// main column, which runs past 800px on a wide desktop — enough to strand this 12px copy in
// one edge-to-edge line. A one-off reading measure with no design-system meaning: kept a
// literal, like `.quality` above, rather than promoted to a $size-* token nothing else
// would ever consume.
.pitch {
    max-width: 560px;
}

// The tab's only heading for a free member, so it takes the same slot in the outline as
// the premium branch's "External Players" (hence the shared `.subtitle`) and one step up
// the type scale — this is the whole page, not a section of it.
// `.pitch-title` is intentionally absent. Its only element also carries `.subtitle`,
// and `_base-section`'s leaked `.subtitle` — a later stylesheet — outranked it, so its
// `@include h5` and margin never rendered. Reinstating them here would be a design
// change, not a migration fix.

// On a phone the pitch title centers itself over the copy, so a left-flush CTA reads
// as misaligned; centre it under the text. Desktop keeps the left-aligned Figma layout.
.pitch-cta {
    @include mobileOnly {
        display: flex;
        justify-content: center;
    }
}

</style>
